<template>
  <div class="user-profile-container">
    <div class="max-w-5xl mx-auto p-4 sm:p-6">
      <!-- Banner de advertencia: Módulo en fase de prueba -->
      <div class="mb-4 bg-amber-50 dark:bg-gray-900 border-2 border-amber-300 dark:border-amber-500 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3 shadow-sm">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-md">
            <Icon icon="mdi:flask" class="text-2xl animate-pulse" />
          </div>
          <div>
            <h3 class="text-xs sm:text-sm font-black text-amber-900 dark:text-amber-300 uppercase tracking-wide flex items-center gap-2">
              <span>Módulo en Fase de Pruebas</span>
              <span class="px-2 py-0.5 bg-amber-200 dark:bg-amber-500 text-amber-900 dark:text-white rounded-full text-[10px] font-black">BETA</span>
            </h3>
            <p class="text-[11px] sm:text-xs text-gray-800 dark:text-gray-100 mt-0.5 font-medium">
              Este módulo se encuentra en período de prueba y verificación. El conteo o monto de reparaciones y ventas podría presentar inconsistencias o estar alterado.
            </p>
          </div>
        </div>
        <button
          @click="mostrarModalPrueba = true"
          class="flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-black text-xs rounded-xl transition-all shadow hover:shadow-md flex items-center gap-1 cursor-pointer"
        >
          <Icon icon="mdi:information-outline" class="text-base" />
          <span>Ver Aviso</span>
        </button>
      </div>
      <!-- Header del perfil -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <!-- Cover -->
        <div class="relative h-32 sm:h-48 bg-gradient-to-r from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800">
          <div class="absolute -bottom-12 sm:-bottom-16 left-4 sm:left-8">
            <div class="relative">
              <div class="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white dark:border-gray-800 bg-white dark:bg-gray-800 overflow-hidden shadow-xl">
                <img v-if="userData.fotoPerfilUrl && !imageError" :src="userData.fotoPerfilUrl" class="w-full h-full object-cover" alt="Foto" @error="handleImageError" />
                <div v-else class="w-full h-full flex items-center justify-center" :style="{ backgroundColor: avatarColor }">
                  <span class="text-2xl sm:text-4xl font-bold text-white">{{ avatarInitials }}</span>
                </div>
              </div>
              <button v-if="isOwnProfile" @click="editProfile" class="absolute bottom-0 right-0 bg-white dark:bg-gray-700 rounded-full p-1.5 sm:p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                <Icon icon="mdi:pencil" class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        <!-- Info del usuario -->
        <div class="pt-14 sm:pt-20 px-4 sm:px-8 pb-6 sm:pb-8">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div class="min-w-0">
              <h1 class="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white truncate">{{ userData.nombre }} {{ userData.apellido }}</h1>
              <p class="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                {{ formatRole(userData.nombreRol) }}
                <span v-if="userData.especialidad" class="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs sm:text-sm">{{ userData.especialidad }}</span>
              </p>
              <div class="flex items-center mt-2 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:email" class="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                <span class="text-xs sm:text-sm truncate">{{ userData.email }}</span>
              </div>
              <div v-if="userData.telefono" class="flex items-center mt-1 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:phone" class="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                <span class="text-xs sm:text-sm">{{ userData.telefono }}</span>
              </div>
              <div v-if="userData.nombreUsuario" class="flex items-center mt-1 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:account" class="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                <span class="text-xs sm:text-sm">@{{ userData.nombreUsuario }}</span>
              </div>
            </div>
            <div class="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
              <span :class="{ 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': userData.estado === 'Activo', 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': userData.estado !== 'Activo' }" class="px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium">
                {{ userData.estado || 'Activo' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- CARD: REPARACIONES (Taller) -->
      <!-- ============================================= -->
      <div class="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-violet-600 to-purple-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2 sm:gap-3">
            <div class="w-9 h-9 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon icon="mdi:tools" class="text-white text-lg sm:text-2xl" />
            </div>
            <div>
              <h2 class="text-white font-black text-sm sm:text-lg">Reparaciones Realizadas</h2>
              <p class="text-violet-200 text-[9px] sm:text-xs font-bold uppercase tracking-widest">Taller y Servicio Técnico</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-[9px] sm:text-xs font-black text-violet-200 uppercase tracking-widest">Comisión</label>
            <div class="relative">
              <input v-model.number="comisionReparaciones" type="number" min="0" max="100" class="w-14 sm:w-20 bg-white/20 border border-white/30 rounded-lg px-2 py-1 text-white font-black text-xs sm:text-sm outline-none focus:bg-white/30 text-center" />
              <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/70 text-[9px] sm:text-xs font-black pointer-events-none">%</span>
            </div>
          </div>
        </div>

        <div class="p-4 sm:p-6">
          <!-- Tarjetas resumen -->
          <div class="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
            <div class="text-center bg-violet-50 dark:bg-violet-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-violet-600 dark:text-violet-400 uppercase tracking-widest">Total Rep.</div>
              <div class="text-xl sm:text-3xl font-black text-violet-600 dark:text-violet-400">{{ estadisticasReparaciones.total || 0 }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{{ getPeriodoTexto() }}</div>
            </div>
            <div class="text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Facturado</div>
              <div class="text-sm sm:text-2xl font-black text-emerald-600 dark:text-emerald-400">${{ estadisticasReparaciones.totalFacturado || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Prom: ${{ estadisticasReparaciones.promedio || '0.00' }}</div>
            </div>
            <div class="text-center bg-violet-50 dark:bg-violet-900/20 rounded-lg p-2 sm:p-4 border-2 border-violet-300 dark:border-violet-700">
              <div class="text-[9px] sm:text-xs font-black text-violet-600 dark:text-violet-400 uppercase tracking-widest">Comisión</div>
              <div class="text-sm sm:text-2xl font-black text-violet-600 dark:text-violet-400">${{ estadisticasReparaciones.comision || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Al {{ comisionReparaciones }}%</div>
            </div>
          </div>

          <!-- Lista / tabla -->
          <div v-if="loadingData" class="text-center py-6">
            <Icon icon="mdi:loading" class="w-6 h-6 animate-spin text-violet-500 mx-auto" />
            <p class="text-xs text-gray-400 mt-2">Cargando reparaciones...</p>
          </div>
          <div v-else-if="reparacionesFiltradas.length === 0" class="text-center py-6 text-gray-400 text-xs sm:text-sm">
            <Icon icon="mdi:tools" class="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Sin reparaciones registradas para este usuario</p>
          </div>
          <div v-else class="overflow-x-auto custom-scrollbar">
            <!-- Desktop: tabla -->
            <table class="hidden sm:table w-full text-left">
              <thead>
                <tr class="border-b border-gray-200 dark:border-gray-700">
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Orden</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Cliente</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Monto</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Comisión</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Estado</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Fecha</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in reparacionesPaginadas" :key="item.idRecepcionEquipo || item.numeroOrden" class="border-b border-gray-100 dark:border-gray-700/50 hover:bg-violet-50/30 dark:hover:bg-gray-700/30">
                  <td class="py-2.5 text-sm font-bold text-violet-600 dark:text-violet-400">#{{ obtenerNumeroOrden(item) }}</td>
                  <td class="py-2.5 text-sm text-gray-700 dark:text-gray-300">{{ obtenerCliente(item) }}</td>
                  <td class="py-2.5 text-sm font-black text-gray-900 dark:text-white text-right">${{ obtenerMonto(item, 'reparacion').toFixed(2) }}</td>
                  <td class="py-2.5 text-sm font-black text-violet-600 dark:text-violet-400 text-right">${{ calcularComision(obtenerMonto(item, 'reparacion'), comisionReparaciones) }}</td>
                  <td class="py-2.5 text-center">
                    <span :class="getEstadoBadgeClass(obtenerEstado(item))" class="inline-block px-2 py-0.5 rounded-lg text-[10px] font-black uppercase">{{ obtenerEstado(item) }}</span>
                  </td>
                  <td class="py-2.5 text-xs text-gray-500 dark:text-gray-400">{{ formatFechaCorta(obtenerFecha(item)) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-violet-50/50 dark:bg-violet-900/20">
                <tr>
                  <td colspan="3" class="py-2.5 text-xs font-black text-gray-500 uppercase text-right">Total Comisión:</td>
                  <td class="py-2.5 text-sm font-black text-violet-600 dark:text-violet-400 text-right">${{ estadisticasReparaciones.comision }}</td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            </table>

            <!-- Mobile: cards -->
            <div class="sm:hidden space-y-2">
              <div v-for="item in reparacionesPaginadas" :key="item.idRecepcionEquipo || item.numeroOrden" class="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-sm font-black text-violet-600 dark:text-violet-400">#{{ obtenerNumeroOrden(item) }}</span>
                  <span :class="getEstadoBadgeClass(obtenerEstado(item))" class="px-2 py-0.5 rounded text-[9px] font-black uppercase">{{ obtenerEstado(item) }}</span>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-300 mb-1.5 truncate">{{ obtenerCliente(item) }}</p>
                <div class="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div class="text-[9px] text-gray-400 uppercase font-black">Monto</div>
                    <div class="text-sm font-black text-gray-900 dark:text-white">${{ obtenerMonto(item, 'reparacion').toFixed(2) }}</div>
                  </div>
                  <div class="text-right">
                    <div class="text-[9px] text-gray-400 uppercase font-black">Comisión {{ comisionReparaciones }}%</div>
                    <div class="text-sm font-black text-violet-600 dark:text-violet-400">${{ calcularComision(obtenerMonto(item, 'reparacion'), comisionReparaciones) }}</div>
                  </div>
                </div>
                <div class="text-[10px] text-gray-400 mt-1">Fecha: {{ formatFechaCorta(obtenerFecha(item)) }}</div>
              </div>
            </div>

            <!-- Paginación Reparaciones -->
            <div v-if="reparacionesFiltradas.length > porPaginaReparaciones" class="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div class="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold">
                <span>Mostrando {{ (paginaReparaciones - 1) * porPaginaReparaciones + 1 }} - {{ Math.min(paginaReparaciones * porPaginaReparaciones, reparacionesFiltradas.length) }} de {{ reparacionesFiltradas.length }}</span>
                <span class="text-gray-300 dark:text-gray-600">|</span>
                <select v-model.number="porPaginaReparaciones" @change="paginaReparaciones = 1" class="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                  <option :value="5">5 / pág</option>
                  <option :value="10">10 / pág</option>
                  <option :value="20">20 / pág</option>
                </select>
              </div>
              <div class="flex items-center gap-1">
                <button
                  @click="paginaReparaciones--"
                  :disabled="paginaReparaciones === 1"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página anterior"
                >
                  <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
                </button>
                <div class="hidden sm:flex items-center gap-1">
                  <button
                    v-for="page in paginasVisiblesReparaciones"
                    :key="page"
                    @click="paginaReparaciones = page"
                    :class="[
                      'w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-xs font-black transition-all flex items-center justify-center',
                      paginaReparaciones === page
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>
                <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">
                  {{ paginaReparaciones }} / {{ totalPaginasReparaciones }}
                </span>
                <button
                  @click="paginaReparaciones++"
                  :disabled="paginaReparaciones === totalPaginasReparaciones"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página siguiente"
                >
                  <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- CARD: VENTAS (Productos) -->
      <!-- ============================================= -->
      <div class="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-emerald-600 to-teal-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2 sm:gap-3">
            <div class="w-9 h-9 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon icon="mdi:cart" class="text-white text-lg sm:text-2xl" />
            </div>
            <div>
              <h2 class="text-white font-black text-sm sm:text-lg">Ventas Realizadas</h2>
              <p class="text-emerald-100 text-[9px] sm:text-xs font-bold uppercase tracking-widest">Facturación y Productos</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-[9px] sm:text-xs font-black text-emerald-100 uppercase tracking-widest">Comisión</label>
            <div class="relative">
              <input v-model.number="comisionVentas" type="number" min="0" max="100" class="w-14 sm:w-20 bg-white/20 border border-white/30 rounded-lg px-2 py-1 text-white font-black text-xs sm:text-sm outline-none focus:bg-white/30 text-center" />
              <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/70 text-[9px] sm:text-xs font-black pointer-events-none">%</span>
            </div>
          </div>
        </div>

        <div class="p-4 sm:p-6">
          <div class="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
            <div class="text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Total Ventas</div>
              <div class="text-xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">{{ estadisticasVentas.total || 0 }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{{ getPeriodoTexto() }}</div>
            </div>
            <div class="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Vendido</div>
              <div class="text-sm sm:text-2xl font-black text-blue-600 dark:text-blue-400">${{ estadisticasVentas.totalFacturado || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Prom: ${{ estadisticasVentas.promedio || '0.00' }}</div>
            </div>
            <div class="text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2 sm:p-4 border-2 border-emerald-300 dark:border-emerald-700">
              <div class="text-[9px] sm:text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Comisión</div>
              <div class="text-sm sm:text-2xl font-black text-emerald-600 dark:text-emerald-400">${{ estadisticasVentas.comision || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Al {{ comisionVentas }}%</div>
            </div>
          </div>

          <div v-if="loadingData" class="text-center py-6">
            <Icon icon="mdi:loading" class="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
            <p class="text-xs text-gray-400 mt-2">Cargando ventas...</p>
          </div>
          <div v-else-if="ventasFiltradas.length === 0" class="text-center py-6 text-gray-400 text-xs sm:text-sm">
            <Icon icon="mdi:cart-outline" class="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Sin ventas registradas para este usuario</p>
          </div>
          <div v-else class="overflow-x-auto custom-scrollbar">
            <!-- Desktop: tabla -->
            <table class="hidden sm:table w-full text-left">
              <thead>
                <tr class="border-b border-gray-200 dark:border-gray-700">
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Factura</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Cliente</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Monto</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Comisión</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Estado</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Fecha</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in ventasPaginadas" :key="v.idVenta || v.numeroFactura" class="border-b border-gray-100 dark:border-gray-700/50 hover:bg-emerald-50/30 dark:hover:bg-gray-700/30">
                  <td class="py-2.5 text-sm font-bold text-emerald-600 dark:text-emerald-400">#{{ obtenerNumeroFactura(v) }}</td>
                  <td class="py-2.5 text-sm text-gray-700 dark:text-gray-300">{{ obtenerCliente(v) }}</td>
                  <td class="py-2.5 text-sm font-black text-gray-900 dark:text-white text-right">${{ obtenerMonto(v, 'venta').toFixed(2) }}</td>
                  <td class="py-2.5 text-sm font-black text-emerald-600 dark:text-emerald-400 text-right">${{ calcularComision(obtenerMonto(v, 'venta'), comisionVentas) }}</td>
                  <td class="py-2.5 text-center">
                    <span :class="v.tipoVenta === 'credito' || v.tipo_venta === 'credito' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'" class="inline-block px-2 py-0.5 rounded-lg text-[10px] font-black uppercase">{{ obtenerEstado(v) }}</span>
                  </td>
                  <td class="py-2.5 text-xs text-gray-500 dark:text-gray-400">{{ formatFechaCorta(obtenerFecha(v)) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-emerald-50/50 dark:bg-emerald-900/20">
                <tr>
                  <td colspan="3" class="py-2.5 text-xs font-black text-gray-500 uppercase text-right">Total Comisión:</td>
                  <td class="py-2.5 text-sm font-black text-emerald-600 dark:text-emerald-400 text-right">${{ estadisticasVentas.comision }}</td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            </table>

            <!-- Mobile: cards -->
            <div class="sm:hidden space-y-2">
              <div v-for="v in ventasPaginadas" :key="v.idVenta || v.numeroFactura" class="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-sm font-black text-emerald-600 dark:text-emerald-400">#{{ obtenerNumeroFactura(v) }}</span>
                  <span :class="v.tipoVenta === 'credito' || v.tipo_venta === 'credito' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'" class="px-2 py-0.5 rounded text-[9px] font-black uppercase">{{ obtenerEstado(v) }}</span>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-300 mb-1.5 truncate">{{ obtenerCliente(v) }}</p>
                <div class="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div class="text-[9px] text-gray-400 uppercase font-black">Monto</div>
                    <div class="text-sm font-black text-gray-900 dark:text-white">${{ obtenerMonto(v, 'venta').toFixed(2) }}</div>
                  </div>
                  <div class="text-right">
                    <div class="text-[9px] text-gray-400 uppercase font-black">Comisión {{ comisionVentas }}%</div>
                    <div class="text-sm font-black text-emerald-600 dark:text-emerald-400">${{ calcularComision(obtenerMonto(v, 'venta'), comisionVentas) }}</div>
                  </div>
                </div>
                <div class="text-[10px] text-gray-400 mt-1">{{ formatFechaCorta(obtenerFecha(v)) }}</div>
              </div>
            </div>

            <!-- Paginación Ventas -->
            <div v-if="ventasFiltradas.length > porPaginaVentas" class="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div class="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold">
                <span>Mostrando {{ (paginaVentas - 1) * porPaginaVentas + 1 }} - {{ Math.min(paginaVentas * porPaginaVentas, ventasFiltradas.length) }} de {{ ventasFiltradas.length }}</span>
                <span class="text-gray-300 dark:text-gray-600">|</span>
                <select v-model.number="porPaginaVentas" @change="paginaVentas = 1" class="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                  <option :value="5">5 / pág</option>
                  <option :value="10">10 / pág</option>
                  <option :value="20">20 / pág</option>
                </select>
              </div>
              <div class="flex items-center gap-1">
                <button
                  @click="paginaVentas--"
                  :disabled="paginaVentas === 1"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página anterior"
                >
                  <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
                </button>
                <div class="hidden sm:flex items-center gap-1">
                  <button
                    v-for="page in paginasVisiblesVentas"
                    :key="page"
                    @click="paginaVentas = page"
                    :class="[
                      'w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-xs font-black transition-all flex items-center justify-center',
                      paginaVentas === page
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>
                <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">
                  {{ paginaVentas }} / {{ totalPaginasVentas }}
                </span>
                <button
                  @click="paginaVentas++"
                  :disabled="paginaVentas === totalPaginasVentas"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página siguiente"
                >
                  <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- CARD: SERVICIOS EXTERNOS (si existen) -->
      <!-- ============================================= -->
      <div v-if="todosServicios.length > 0" class="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div class="bg-gradient-to-r from-cyan-600 to-blue-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-wrap gap-2">
          <div class="flex items-center gap-2 sm:gap-3">
            <div class="w-9 h-9 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon icon="mdi:home-wrench" class="text-white text-lg sm:text-2xl" />
            </div>
            <div>
              <h2 class="text-white font-black text-sm sm:text-lg">Servicios Externos</h2>
              <p class="text-cyan-100 text-[9px] sm:text-xs font-bold uppercase tracking-widest">A Domicilio</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-[9px] sm:text-xs font-black text-cyan-100 uppercase tracking-widest">Comisión</label>
            <div class="relative">
              <input v-model.number="comisionServicios" type="number" min="0" max="100" class="w-14 sm:w-20 bg-white/20 border border-white/30 rounded-lg px-2 py-1 text-white font-black text-xs sm:text-sm outline-none focus:bg-white/30 text-center" />
              <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/70 text-[9px] sm:text-xs font-black pointer-events-none">%</span>
            </div>
          </div>
        </div>

        <div class="p-4 sm:p-6">
          <div class="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
            <div class="text-center bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">Total Serv.</div>
              <div class="text-xl sm:text-3xl font-black text-cyan-600 dark:text-cyan-400">{{ estadisticasServicios.total || 0 }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{{ getPeriodoTexto() }}</div>
            </div>
            <div class="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 sm:p-4">
              <div class="text-[9px] sm:text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Facturado</div>
              <div class="text-sm sm:text-2xl font-black text-blue-600 dark:text-blue-400">${{ estadisticasServicios.totalFacturado || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Prom: ${{ estadisticasServicios.promedio || '0.00' }}</div>
            </div>
            <div class="text-center bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-2 sm:p-4 border-2 border-cyan-300 dark:border-cyan-700">
              <div class="text-[9px] sm:text-xs font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">Comisión</div>
              <div class="text-sm sm:text-2xl font-black text-cyan-600 dark:text-cyan-400">${{ estadisticasServicios.comision || '0.00' }}</div>
              <div class="text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Al {{ comisionServicios }}%</div>
            </div>
          </div>

          <div v-if="serviciosFiltrados.length === 0" class="text-center py-6 text-gray-400 text-xs sm:text-sm">
            <Icon icon="mdi:home-wrench" class="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Sin servicios registrados para este usuario</p>
          </div>
          <div v-else class="overflow-x-auto custom-scrollbar">
            <table class="hidden sm:table w-full text-left">
              <thead>
                <tr class="border-b border-gray-200 dark:border-gray-700">
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Servicio</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Cliente</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Monto</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Comisión</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Estado</th>
                  <th class="py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Fecha</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in serviciosPaginados" :key="s.idServicioExterno || s.numeroServicio" class="border-b border-gray-100 dark:border-gray-700/50 hover:bg-cyan-50/30">
                  <td class="py-2.5 text-sm font-bold text-cyan-600 dark:text-cyan-400">#{{ s.numeroServicio || s.idServicioExterno }}</td>
                  <td class="py-2.5 text-sm text-gray-700 dark:text-gray-300">{{ obtenerCliente(s) }}</td>
                  <td class="py-2.5 text-sm font-black text-gray-900 dark:text-white text-right">${{ obtenerMonto(s, 'servicio').toFixed(2) }}</td>
                  <td class="py-2.5 text-sm font-black text-cyan-600 dark:text-cyan-400 text-right">${{ calcularComision(obtenerMonto(s, 'servicio'), comisionServicios) }}</td>
                  <td class="py-2.5 text-center">
                    <span :class="getEstadoServicioBadgeClass(s.estado)" class="inline-block px-2 py-0.5 rounded-lg text-[10px] font-black uppercase">{{ s.estado || '---' }}</span>
                  </td>
                  <td class="py-2.5 text-xs text-gray-500 dark:text-gray-400">{{ formatFechaCorta(obtenerFecha(s)) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-cyan-50/50 dark:bg-cyan-900/20">
                <tr>
                  <td colspan="3" class="py-2.5 text-xs font-black text-gray-500 uppercase text-right">Total Comisión:</td>
                  <td class="py-2.5 text-sm font-black text-cyan-600 dark:text-cyan-400 text-right">${{ estadisticasServicios.comision }}</td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            </table>

            <div class="sm:hidden space-y-2">
              <div v-for="s in serviciosPaginados" :key="s.idServicioExterno || s.numeroServicio" class="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-sm font-black text-cyan-600 dark:text-cyan-400">#{{ s.numeroServicio || s.idServicioExterno }}</span>
                  <span :class="getEstadoServicioBadgeClass(s.estado)" class="px-2 py-0.5 rounded text-[9px] font-black uppercase">{{ s.estado || '---' }}</span>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-300 mb-1.5 truncate">{{ obtenerCliente(s) }}</p>
                <div class="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <div class="text-[9px] text-gray-400 uppercase font-black">Monto</div>
                    <div class="text-sm font-black text-gray-900 dark:text-white">${{ obtenerMonto(s, 'servicio').toFixed(2) }}</div>
                  </div>
                  <div class="text-right">
                    <div class="text-[9px] text-gray-400 uppercase font-black">Comisión {{ comisionServicios }}%</div>
                    <div class="text-sm font-black text-cyan-600 dark:text-cyan-400">${{ calcularComision(obtenerMonto(s, 'servicio'), comisionServicios) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Paginación Servicios -->
            <div v-if="serviciosFiltrados.length > porPaginaServicios" class="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div class="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold">
                <span>Mostrando {{ (paginaServicios - 1) * porPaginaServicios + 1 }} - {{ Math.min(paginaServicios * porPaginaServicios, serviciosFiltrados.length) }} de {{ serviciosFiltrados.length }}</span>
                <span class="text-gray-300 dark:text-gray-600">|</span>
                <select v-model.number="porPaginaServicios" @change="paginaServicios = 1" class="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                  <option :value="5">5 / pág</option>
                  <option :value="10">10 / pág</option>
                  <option :value="20">20 / pág</option>
                </select>
              </div>
              <div class="flex items-center gap-1">
                <button
                  @click="paginaServicios--"
                  :disabled="paginaServicios === 1"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página anterior"
                >
                  <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
                </button>
                <div class="hidden sm:flex items-center gap-1">
                  <button
                    v-for="page in paginasVisiblesServicios"
                    :key="page"
                    @click="paginaServicios = page"
                    :class="[
                      'w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-xs font-black transition-all flex items-center justify-center',
                      paginaServicios === page
                        ? 'bg-cyan-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    ]"
                  >
                    {{ page }}
                  </button>
                </div>
                <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">
                  {{ paginaServicios }} / {{ totalPaginasServicios }}
                </span>
                <button
                  @click="paginaServicios++"
                  :disabled="paginaServicios === totalPaginasServicios"
                  class="p-1 sm:p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                  title="Página siguiente"
                >
                  <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- CARD TOTAL: LO QUE CORRESPONDE RECIBIR -->
      <!-- ============================================= -->
      <div class="mt-6 bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl shadow-lg p-4 sm:p-6 text-white">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-2 sm:gap-4">
            <div class="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon icon="mdi:wallet" class="text-white text-2xl sm:text-4xl" />
            </div>
            <div>
              <p class="text-[10px] sm:text-xs font-black uppercase tracking-widest text-violet-200">Total Comisiones Acumuladas</p>
              <p class="text-xs sm:text-sm font-bold text-violet-100">Histórico general</p>
            </div>
          </div>
          <div class="text-right">
            <p class="text-2xl sm:text-4xl font-black">${{ totalComision.toFixed(2) }}</p>
            <p class="text-[10px] sm:text-xs font-bold text-violet-200 mt-0.5">Reparaciones + Ventas + Servicios</p>
          </div>
        </div>
      </div>

      <!-- Acciones -->
      <div class="mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
        <button v-if="isOwnProfile" @click="changePassword" class="w-full sm:w-auto px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center font-bold text-xs sm:text-sm">
          <Icon icon="mdi:key" class="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Cambiar Contraseña
        </button>
        <button v-if="isOwnProfile" @click="editProfile" class="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center font-bold text-xs sm:text-sm shadow-md">
          <Icon icon="mdi:pencil" class="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Editar Perfil
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL: MÓDULO EN PRUEBA -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div
        v-if="mostrarModalPrueba"
        class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
        @click.self="cerrarModalPrueba"
      >
        <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-amber-300 dark:border-amber-600/50 animate-fadeIn animate-slideUp">
          <!-- Header del Modal -->
          <div class="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-6 py-5 text-white flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-inner">
                <Icon icon="mdi:alert-octagon" class="text-2xl text-white" />
              </div>
              <div>
                <span class="text-[10px] font-black uppercase tracking-widest text-amber-100">Aviso Oficial</span>
                <h3 class="text-lg font-black leading-tight">Módulo en Prueba</h3>
              </div>
            </div>
            <button
              @click="cerrarModalPrueba"
              class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
              title="Cerrar"
            >
              <Icon icon="mdi:close" class="text-lg" />
            </button>
          </div>

          <!-- Contenido del Modal -->
          <div class="p-6">
            <div class="flex items-start gap-3 bg-amber-50 dark:bg-gray-900 p-4 rounded-2xl border-2 border-amber-300 dark:border-amber-500 mb-4 shadow-sm">
              <Icon icon="mdi:alert-circle" class="text-amber-600 dark:text-amber-400 text-3xl flex-shrink-0 mt-0.5" />
              <div class="text-xs sm:text-sm text-gray-900 dark:text-white leading-relaxed font-medium">
                <p class="font-black text-amber-900 dark:text-amber-400 mb-1 text-sm sm:text-base">
                  Atención: Datos en período de verificación
                </p>
                <p class="text-gray-900 dark:text-white">
                  Este módulo de <strong>Perfil y Comisiones</strong> se encuentra actualmente en <strong>fase de pruebas</strong>.
                </p>
                <p class="mt-2 text-gray-900 dark:text-gray-100">
                  Puede ser que el registro de <strong>reparaciones o ventas esté alterado</strong> o presente variaciones, debido a que el módulo aún no se ha probado en su totalidad y podría contener errores o diferencias con los registros originales.
                </p>
              </div>
            </div>

            <div class="space-y-2 text-xs text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-900/80 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-2 font-medium">
                <Icon icon="mdi:shield-alert" class="text-amber-500 text-base flex-shrink-0" />
                <span>Los datos mostrados tienen carácter informativo y de prueba.</span>
              </div>
              <div class="flex items-center gap-2 font-medium">
                <Icon icon="mdi:tools" class="text-violet-500 dark:text-violet-400 text-base flex-shrink-0" />
                <span>Solo se contabilizan reparaciones donde fuiste el técnico reparador.</span>
              </div>
            </div>

            <!-- Botón de acción -->
            <div class="mt-6">
              <button
                @click="cerrarModalPrueba"
                class="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-black rounded-2xl shadow-lg shadow-amber-500/25 transition-all flex items-center justify-center gap-2 text-sm active:scale-95 cursor-pointer"
              >
                <Icon icon="mdi:checkbox-marked-circle" class="text-lg" />
                Entendido, Continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { apiFetch, getCurrentServer } from '@/api/index.js';
import * as recepcionApi from '@/api/recepcionEquipo.js';
import * as ventasApi from '@/api/ventas.js';
import * as serviciosExternosApi from '@/api/serviciosExternos.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';

export default {
  name: 'UserProfile',
  components: { Icon },
  props: {
    userId: { type: [Number, String], default: null },
    isOwnProfile: { type: Boolean, default: true }
  },
  data() {
    return {
      userData: {
        idUsuario: null, nombre: '', apellido: '', email: '',
        nombreUsuario: '', nombreRol: '', especialidad: '', telefono: '',
        estado: 'Activo', fotoPerfilUrl: null
      },
      todasReparaciones: [],
      todosServicios: [],
      todasVentas: [],
      imageError: false,
      loadingData: false,
      // En este sistema cada usuario puede ser vendedor y técnico
      esTecnico: true,
      esVendedor: true,
      periodoSeleccionado: 'total',
      // Comisiones separadas por tipo
      comisionReparaciones: 10,
      comisionVentas: 5,
      comisionServicios: 10,
      
      idTecnicoDelUsuario: null,
      idVendedorDelUsuario: null,

      // Paginación por sección
      paginaReparaciones: 1,
      porPaginaReparaciones: 5,
      paginaVentas: 1,
      porPaginaVentas: 5,
      paginaServicios: 1,
      porPaginaServicios: 5,
      mostrarModalPrueba: true
    };
  },
  computed: {
    avatarInitials() {
      if (!this.userData.nombre && !this.userData.apellido) return 'U';
      const f = this.userData.nombre ? this.userData.nombre.charAt(0) : '';
      const l = this.userData.apellido ? this.userData.apellido.charAt(0) : '';
      return (f + l).toUpperCase() || 'U';
    },
    avatarColor() {
      const initials = this.avatarInitials;
      if (!initials) return '#4F46E5';
      const colors = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#06B6D4', '#84CC16', '#F97316'];
      let hash = 0;
      for (let i = 0; i < initials.length; i++) hash = initials.charCodeAt(i) + ((hash << 5) - hash);
      return colors[Math.abs(hash) % colors.length];
    },
    
    // Muestra todas las reparaciones, servicios y ventas realizadas
    reparacionesFiltradas() { return this.filtrarPorPeriodo(this.todasReparaciones); },
    serviciosFiltrados() { return this.filtrarPorPeriodo(this.todosServicios); },
    ventasFiltradas() { return this.filtrarPorPeriodo(this.todasVentas); },
    
    // Paginación Reparaciones
    totalPaginasReparaciones() {
      return Math.ceil(this.reparacionesFiltradas.length / this.porPaginaReparaciones) || 1;
    },
    reparacionesPaginadas() {
      const start = (this.paginaReparaciones - 1) * this.porPaginaReparaciones;
      return this.reparacionesFiltradas.slice(start, start + this.porPaginaReparaciones);
    },
    paginasVisiblesReparaciones() {
      return this.calcularPaginasVisibles(this.totalPaginasReparaciones, this.paginaReparaciones);
    },

    // Paginación Ventas
    totalPaginasVentas() {
      return Math.ceil(this.ventasFiltradas.length / this.porPaginaVentas) || 1;
    },
    ventasPaginadas() {
      const start = (this.paginaVentas - 1) * this.porPaginaVentas;
      return this.ventasFiltradas.slice(start, start + this.porPaginaVentas);
    },
    paginasVisiblesVentas() {
      return this.calcularPaginasVisibles(this.totalPaginasVentas, this.paginaVentas);
    },

    // Paginación Servicios
    totalPaginasServicios() {
      return Math.ceil(this.serviciosFiltrados.length / this.porPaginaServicios) || 1;
    },
    serviciosPaginados() {
      const start = (this.paginaServicios - 1) * this.porPaginaServicios;
      return this.serviciosFiltrados.slice(start, start + this.porPaginaServicios);
    },
    paginasVisiblesServicios() {
      return this.calcularPaginasVisibles(this.totalPaginasServicios, this.paginaServicios);
    },

    estadisticasReparaciones() {
      return this.calcularEstadisticas(this.reparacionesFiltradas, 'reparacion', this.comisionReparaciones);
    },
    estadisticasServicios() {
      return this.calcularEstadisticas(this.serviciosFiltrados, 'servicio', this.comisionServicios);
    },
    estadisticasVentas() {
      return this.calcularEstadisticas(this.ventasFiltradas, 'venta', this.comisionVentas);
    },
    
    totalComision() {
      return parseFloat(this.estadisticasReparaciones.comision || 0) +
             parseFloat(this.estadisticasServicios.comision || 0) +
             parseFloat(this.estadisticasVentas.comision || 0);
    }
  },
  methods: {
    calcularPaginasVisibles(total, current) {
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
      if (current <= 3) return [1, 2, 3, 4, 5];
      if (current >= total - 2) return [total - 4, total - 3, total - 2, total - 1, total];
      return [current - 2, current - 1, current, current + 1, current + 2];
    },

    async fetchUserImageUrl(usuarioId) {
      try {
        const imagenes = await apiFetch('/Imagen/listar/usuario');
        const list = Array.isArray(imagenes) ? imagenes : (imagenes?.data || []);
        const img = list.find(i => i.entidadId === usuarioId);
        if (img) return img.url;
      } catch (e) { console.error(e); }
      return null;
    },
    
    async loadUserData() {
      try {
        let userData = null;
        if (this.userId) {
          try {
            const res = await apiFetch(`/Usuario/${this.userId}`);
            userData = res?.data || res;
          } catch (e) { console.error('Error fetching user by ID:', e); }
        }
        
        if (!userData) {
          const userNewStr = localStorage.getItem('user');
          if (userNewStr) userData = JSON.parse(userNewStr);
          else {
            const userOldStr = localStorage.getItem('user_data');
            if (userOldStr) userData = JSON.parse(userOldStr);
          }
        }
        
        if (!userData) return;
        
        const usuarioId = userData.idUsuario || userData.id;
        const imagenUrl = await this.fetchUserImageUrl(usuarioId);
        
        this.userData = {
          idUsuario: usuarioId,
          nombre: userData.nombre || '',
          apellido: userData.apellido || '',
          email: userData.email || '',
          nombreUsuario: userData.nombreUsuario || userData.username || '',
          nombreRol: userData.nombreRol || userData.rol?.nombre || 'Usuario',
          especialidad: userData.especialidad || '',
          telefono: userData.telefono || '',
          estado: userData.estado || 'Activo',
          fotoPerfilUrl: imagenUrl
        };
        
        if (!this.userData.fotoPerfilUrl && userData.fotoPerfil) {
          this.userData.fotoPerfilUrl = `${getCurrentServer()}/imagenes/usuario/${userData.fotoPerfil}`;
        }
        
        // Cada usuario en el sistema es vendedor y técnico
        this.esVendedor = true;
        this.esTecnico = true;
        
        // Comisión configurada del usuario si existe
        const comisionDefault = parseFloat(userData.comisionPorcentaje || userData.comision_porcentaje || 0);
        if (comisionDefault > 0) {
          this.comisionReparaciones = comisionDefault;
          this.comisionVentas = comisionDefault;
          this.comisionServicios = comisionDefault;
        }
        
        // Mapear identificadores de técnico y vendedor si existen en sus respectivas tablas
        await this.mapearIds(usuarioId);
        
        // Cargar SIEMPRE tanto las reparaciones como las ventas y los servicios
        await Promise.all([
          this.cargarReparacionesDelTecnico(usuarioId),
          this.cargarVentasDelVendedor(usuarioId),
          this.cargarServiciosDelTecnico(usuarioId)
        ]);
        
      } catch (error) {
        console.error('❌ Error cargando datos del usuario:', error);
      }
    },
    
    async mapearIds(usuarioId) {
      const uid = parseInt(usuarioId);
      const userNombre = (this.userData.nombre || '').toLowerCase().trim();

      try {
        const tecRes = await obtenerTecnicos();
        const tecnicos = Array.isArray(tecRes) ? tecRes : (tecRes?.data || []);
        const tecnico = tecnicos.find(t => {
          const tIdU = parseInt(t.idUsuario || t.IdUsuario);
          const tIdT = parseInt(t.idTecnico || t.IdTecnico);
          const tNom = (t.nombre || '').toLowerCase().trim();
          return tIdU === uid || tIdT === uid || (userNombre && tNom === userNombre);
        });
        if (tecnico) {
          this.idTecnicoDelUsuario = tecnico.idTecnico || tecnico.IdTecnico || tecnico.id;
          console.log(`✅ idUsuario ${usuarioId} → idTecnico ${this.idTecnicoDelUsuario}`);
        }
      } catch (e) { console.error('Error mapeo técnico:', e); }
      
      try {
        const vRes = await apiFetch('/Vendedor');
        const vendedores = Array.isArray(vRes) ? vRes : (vRes?.data || []);
        const vendedor = vendedores.find(v => {
          const vIdU = parseInt(v.idUsuario || v.id_usuario || v.IdUsuario);
          const vIdV = parseInt(v.idVendedor || v.id || v.IdVendedor);
          const vNom = (v.nombre || '').toLowerCase().trim();
          return vIdU === uid || vIdV === uid || (userNombre && vNom === userNombre);
        });
        if (vendedor) {
          this.idVendedorDelUsuario = vendedor.idVendedor || vendedor.id || vendedor.IdVendedor;
          console.log(`✅ idUsuario ${usuarioId} → idVendedor ${this.idVendedorDelUsuario}`);
        }
      } catch (e) { console.error('Error mapeo vendedor:', e); }
    },
    
    // Carga ÚNICAMENTE las reparaciones que este usuario REALMENTE REPARÓ
    // (NO incluye el usuario que recibió en mostrador ni el que entregó en caja)
    async cargarReparacionesDelTecnico(usuarioId) {
      this.loadingData = true;
      try {
        const res = await recepcionApi.obtenerRecepciones();
        const ordenes = Array.isArray(res) ? res : (res?.data || []);
        
        const uid = parseInt(usuarioId);
        const tid = this.idTecnicoDelUsuario ? parseInt(this.idTecnicoDelUsuario) : null;
        const userNombre = (this.userData.nombre || '').toLowerCase().trim();
        const userApellido = (this.userData.apellido || '').toLowerCase().trim();
        const nombreCompleto = `${userNombre} ${userApellido}`.trim();
        
        const reparaciones = ordenes.filter(orden => {
          // Extraer estrictamente el ID del técnico que reparó el equipo
          const idReparador = parseInt(
            orden.idTecnicoReparador ?? 
            orden.IdTecnicoReparador ?? 
            orden.id_tecnico_reparador
          );
          
          // Si tiene ID de técnico reparador registrado, verificar si coincide
          if (idReparador && !isNaN(idReparador)) {
            const esReparador = (tid && idReparador === tid) || (uid && idReparador === uid);
            if (esReparador) return true;
          }
          
          // Fallback por nombre específico del técnico reparador
          const nomRep = (orden.nombreTecnicoReparador || orden.NombreTecnicoReparador || '').toLowerCase().trim();
          if (nomRep) {
            if (nombreCompleto && nomRep.includes(nombreCompleto)) return true;
            if (userNombre && nomRep.includes(userNombre)) return true;
          }
          
          return false;
        });
        
        console.log(`✅ Reparaciones que este usuario REALMENTE reparó: ${reparaciones.length}`);
        this.todasReparaciones = reparaciones;
        this.paginaReparaciones = 1;
      } catch (error) {
        console.error('❌ Error reparaciones:', error);
      } finally {
        this.loadingData = false;
      }
    },
    
    // Carga ÚNICAMENTE los servicios externos asignados a este técnico
    async cargarServiciosDelTecnico(usuarioId) {
      try {
        const api = serviciosExternosApi.default || serviciosExternosApi;
        const res = await api.obtenerServicios();
        const serviciosList = Array.isArray(res) ? res : (res?.data || []);
        
        const uid = parseInt(usuarioId);
        const tid = this.idTecnicoDelUsuario ? parseInt(this.idTecnicoDelUsuario) : null;
        const userNombre = (this.userData.nombre || '').toLowerCase().trim();
        const userApellido = (this.userData.apellido || '').toLowerCase().trim();
        const nombreCompleto = `${userNombre} ${userApellido}`.trim();
        
        const servicios = serviciosList.filter(s => {
          const idT = parseInt(s.idTecnico ?? s.id_tecnico ?? s.IdTecnico);
          
          if (idT && !isNaN(idT)) {
            const esTecnicoServicio = (tid && idT === tid) || (uid && idT === uid);
            if (esTecnicoServicio) return true;
          }
          
          const nomTec = (s.nombreTecnico || s.tecnico?.nombre || '').toLowerCase().trim();
          if (nomTec) {
            if (nombreCompleto && nomTec.includes(nombreCompleto)) return true;
            if (userNombre && nomTec.includes(userNombre)) return true;
          }
          
          return false;
        });
        
        console.log(`✅ Servicios asignados a este técnico: ${servicios.length}`);
        this.todosServicios = servicios;
        this.paginaServicios = 1;
      } catch (error) {
        console.error('❌ Error servicios:', error);
      }
    },
    
    // Carga ÚNICAMENTE las ventas realizadas por este usuario como vendedor
    async cargarVentasDelVendedor(usuarioId) {
      this.loadingData = true;
      try {
        const resTodas = await ventasApi.obtenerVentas();
        const ventasList = Array.isArray(resTodas) ? resTodas : (resTodas?.data || []);
        
        const uid = parseInt(usuarioId);
        const vid = this.idVendedorDelUsuario ? parseInt(this.idVendedorDelUsuario) : null;
        const userNombre = (this.userData.nombre || '').toLowerCase().trim();
        const userApellido = (this.userData.apellido || '').toLowerCase().trim();
        const nombreCompleto = `${userNombre} ${userApellido}`.trim();
        
        const ventas = ventasList.filter(v => {
          const idV = parseInt(
            v.idVendedor ?? 
            v.id_vendedor ?? 
            v.IdVendedor ?? 
            v.idVendedorNavigation?.idVendedor
          );
          
          if (idV && !isNaN(idV)) {
            const esElVendedor = (vid && idV === vid) || (uid && idV === uid);
            if (esElVendedor) return true;
          }
          
          const nomVen = (v.vendedorNombre || v.nombreVendedor || v.vendedor?.nombre || '').toLowerCase().trim();
          if (nomVen) {
            if (nombreCompleto && nomVen.includes(nombreCompleto)) return true;
            if (userNombre && nomVen.includes(userNombre)) return true;
          }
          
          return false;
        });
        
        console.log(`✅ Ventas realizadas por este vendedor: ${ventas.length}`);
        this.todasVentas = ventas;
        this.paginaVentas = 1;
      } catch (error) {
        console.error('❌ Error ventas:', error);
      } finally {
        this.loadingData = false;
      }
    },
    
    filtrarPorPeriodo(items) {
      if (!items || items.length === 0) return [];
      
      // En el perfil se muestran todos los registros acumulados
      if (this.periodoSeleccionado === 'total') {
        return items;
      }
      
      const ahora = new Date();
      ahora.setHours(23, 59, 59, 999);
      
      let fechaInicio = null;
      if (this.periodoSeleccionado === 'diario') {
        fechaInicio = new Date();
        fechaInicio.setHours(0, 0, 0, 0);
      } else if (this.periodoSeleccionado === 'semanal') {
        fechaInicio = new Date();
        fechaInicio.setDate(ahora.getDate() - 7);
        fechaInicio.setHours(0, 0, 0, 0);
      } else if (this.periodoSeleccionado === 'mensual') {
        fechaInicio = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        fechaInicio.setHours(0, 0, 0, 0);
      } else {
        return items;
      }
      
      return items.filter(item => {
        const fechaStr = this.obtenerFecha(item);
        if (!fechaStr) return true;
        const fecha = new Date(fechaStr);
        if (isNaN(fecha.getTime())) return true;
        return fecha >= fechaInicio && fecha <= ahora;
      });
    },
    
    obtenerFecha(item) {
      if (!item) return new Date().toISOString();
      return (
        item.fechaReparacion ||
        item.FechaReparacion ||
        item.fechaVenta ||
        item.fecha_venta ||
        item.fechaFinalizacion ||
        item.fechaEntrega ||
        item.fechaProgramada ||
        item.fechaIngreso ||
        item.fechaCreacion ||
        item.fecha ||
        new Date().toISOString()
      );
    },

    obtenerNumeroOrden(item) {
      if (!item) return '---';
      return item.numeroOrden || item.NumeroOrden || item.numero_orden || item.idRecepcionEquipo || '---';
    },

    obtenerNumeroFactura(item) {
      if (!item) return '---';
      return item.numeroFactura || item.numero_factura || item.idVenta || '---';
    },

    obtenerCliente(item) {
      if (!item) return '---';
      return (
        item.nombreCliente ||
        item.clienteNombre ||
        (item.cliente ? `${item.cliente.nombre || ''} ${item.cliente.apellido || ''}`.trim() : '') ||
        '---'
      );
    },

    obtenerMonto(item, tipo) {
      if (!item) return 0;
      if (tipo === 'reparacion') {
        return parseFloat(item.precioTotalUsd ?? item.precioTotal ?? item.precio_total_usd ?? item.costoEstimado ?? item.total ?? 0) || 0;
      }
      if (tipo === 'servicio') {
        return parseFloat(item.costo ?? item.totalUsd ?? item.montoPagado ?? item.monto ?? 0) || 0;
      }
      return parseFloat(item.totalUsd ?? item.total_usd ?? item.total ?? 0) || 0;
    },

    obtenerEstado(item) {
      if (!item) return 'Completado';
      return item.nombreEstado || item.estado || item.estadoRecepcion?.nombre || 'Completado';
    },
    
    calcularEstadisticas(items, tipo, comisionPct) {
      if (!items || items.length === 0) {
        return { total: 0, totalFacturado: '0.00', promedio: '0.00', comision: '0.00' };
      }
      
      const total = items.length;
      const totalFacturado = items.reduce((sum, item) => {
        return sum + this.obtenerMonto(item, tipo);
      }, 0);
      
      const comision = (totalFacturado * (comisionPct || 0)) / 100;
      
      return {
        total,
        totalFacturado: totalFacturado.toFixed(2),
        promedio: total > 0 ? (totalFacturado / total).toFixed(2) : '0.00',
        comision: comision.toFixed(2)
      };
    },
    
    calcularComision(monto, pct) {
      const m = parseFloat(monto) || 0;
      return ((m * (pct || 0)) / 100).toFixed(2);
    },
    
    getPeriodoTexto() {
      return 'Total acumulado';
    },
    
    getEstadoBadgeClass(estado) {
      const e = (estado || '').toUpperCase();
      if (e.includes('ENTREGADO') || e.includes('COMPLETADO')) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      if (e.includes('REPARADO') || e.includes('LISTO')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      if (e.includes('EN PROCESO')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    },
    
    getEstadoServicioBadgeClass(estado) {
      const e = (estado || '').toUpperCase();
      if (e === 'COMPLETADO') return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      if (e === 'EN PROCESO') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      if (e === 'PENDIENTE') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      if (e === 'CANCELADO') return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    },
    
    formatFechaCorta(fechaISO) {
      if (!fechaISO) return '---';
      try {
        return new Date(fechaISO).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch { return '---'; }
    },
    
    handleImageError() {
      this.imageError = true;
      this.userData.fotoPerfilUrl = null;
    },
    
    formatRole(role) {
      if (!role) return 'Usuario';
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    
    editProfile() {
      this.$router.push('/editar-perfil');
      this.$emit('editProfile');
    },
    
    cerrarModalPrueba() {
      this.mostrarModalPrueba = false;
    },
    abrirModalPrueba() {
      this.mostrarModalPrueba = true;
    },
    changePassword() {
      this.$router.push('/cambiar-contrasena');
      this.$emit('changePassword');
    }
  },
  mounted() {
    this.loadUserData();
    window.addEventListener('storage', (event) => {
      if (event.key === 'user' || event.key === 'user_data') this.loadUserData();
    });
    window.addEventListener('userUpdated', this.loadUserData);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.loadUserData);
    window.removeEventListener('userUpdated', this.loadUserData);
  }
};
</script>

<style scoped>
.user-profile-container { min-height: 100vh; background-color: #f9fafb; }
.dark .user-profile-container { background-color: #111827; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.bg-white, .dark\:bg-gray-800 { animation: fadeIn 0.4s ease-out; }

.custom-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

/* Evita zoom en inputs de iOS */
input[type="number"] { font-size: 16px; }
@media (min-width: 640px) {
  input[type="number"] { font-size: inherit; }
}

/* Remove spinner arrows */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] { -moz-appearance: textfield; }

@media (max-width: 768px) {
  .user-profile-container { padding: 0; }
}
</style>
